import React from 'react';
import { Book, FileText, Info, MessageSquare, Bot, Send, Users, Megaphone } from 'lucide-react';

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string;
  subsections: {
    title: string;
    content: string;
  }[];
}

const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Primeros Pasos',
    icon: <Book className="h-6 w-6 text-blue-500" />,
    content: 'Aprende los conceptos básicos para comenzar a usar la plataforma.',
    subsections: [
      {
        title: 'Crear una cuenta',
        content: 'Para comenzar, registra una cuenta con tu correo electrónico y contraseña. Una vez registrado, podrás acceder a todas las funcionalidades de la plataforma.'
      },
      {
        title: 'Conectar WhatsApp',
        content: 'Conecta tu cuenta de WhatsApp Business siguiendo estos pasos:\n\n1. Haz clic en "Conectar WhatsApp" en la barra superior\n2. Ingresa los datos de tu empresa\n3. Autoriza la conexión con Facebook\n4. ¡Listo! Ya puedes empezar a usar la plataforma'
      }
    ]
  },
  {
    id: 'channels',
    title: 'Canales',
    icon: <MessageSquare className="h-6 w-6 text-green-500" />,
    content: 'Gestiona tus canales de WhatsApp Business.',
    subsections: [
      {
        title: 'Crear un canal',
        content: 'Para crear un nuevo canal:\n\n1. Ve a la sección Canales\n2. Haz clic en "Agregar canal"\n3. Ingresa el nombre del canal\n4. Sigue el proceso de conexión con WhatsApp Business'
      },
      {
        title: 'Configurar un canal',
        content: 'Puedes personalizar la configuración de cada canal:\n\n- Nombre del canal\n- Perfil de empresa\n- Mensajes automáticos\n- Horarios de atención'
      }
    ]
  },
  {
    id: 'chatbots',
    title: 'Chatbots',
    icon: <Bot className="h-6 w-6 text-indigo-500" />,
    content: 'Crea y gestiona chatbots automatizados.',
    subsections: [
      {
        title: 'Crear un flujo',
        content: 'Para crear un nuevo flujo de chatbot:\n\n1. Selecciona un canal\n2. Ve a la sección Chatbot\n3. Haz clic en "Nuevo Flujo"\n4. Usa el editor visual para diseñar tu flujo'
      },
      {
        title: 'Tipos de nodos',
        content: '- Disparador: Inicia el flujo\n- Enviar Mensaje: Envía texto o multimedia\n- Hacer Pregunta: Espera respuesta del usuario\n- Rama: Crea condiciones\n- Saltar: Va a otro paso\n- Petición HTTP: Conecta con APIs\n- Esperar: Pausa el flujo\n- Finalizar: Termina la conversación'
      }
    ]
  },
  {
    id: 'campaigns',
    title: 'Campañas',
    icon: <Megaphone className="h-6 w-6 text-yellow-500" />,
    content: 'Crea y gestiona campañas de mensajes.',
    subsections: [
      {
        title: 'Crear una campaña',
        content: 'Para crear una nueva campaña:\n\n1. Ve a la sección Campañas\n2. Haz clic en "Nueva Campaña"\n3. Selecciona el canal\n4. Define el mensaje y los destinatarios\n5. Programa el envío'
      },
      {
        title: 'Monitorear resultados',
        content: 'Analiza el rendimiento de tus campañas:\n\n- Mensajes enviados\n- Tasa de entrega\n- Tasa de lectura\n- Respuestas recibidas'
      }
    ]
  },
  {
    id: 'contacts',
    title: 'Contactos',
    icon: <Users className="h-6 w-6 text-purple-500" />,
    content: 'Gestiona tu lista de contactos.',
    subsections: [
      {
        title: 'Agregar contactos',
        content: 'Puedes agregar contactos de varias formas:\n\n1. Manualmente uno por uno\n2. Importando un archivo CSV\n3. Automáticamente al recibir mensajes'
      },
      {
        title: 'Organizar contactos',
        content: 'Usa etiquetas para organizar tus contactos:\n\n- Crea etiquetas personalizadas\n- Asigna múltiples etiquetas\n- Filtra por etiquetas\n- Exporta listas filtradas'
      }
    ]
  }
];

export default function Help() {
  const [selectedSection, setSelectedSection] = React.useState<string>('getting-started');

  const currentSection = helpSections.find(section => section.id === selectedSection);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center space-x-2 mb-8">
          <FileText className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Centro de Ayuda</h1>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow">
              <ul className="divide-y divide-gray-200">
                {helpSections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => setSelectedSection(section.id)}
                      className={`w-full px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors ${
                        selectedSection === section.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      {section.icon}
                      <span className={`text-sm font-medium ${
                        selectedSection === section.id ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {section.title}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Content */}
          {currentSection && (
            <div className="flex-1">
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    {currentSection.icon}
                    <h2 className="text-xl font-semibold text-gray-900">
                      {currentSection.title}
                    </h2>
                  </div>
                  <p className="mt-2 text-gray-600">
                    {currentSection.content}
                  </p>
                </div>

                <div className="p-6">
                  <div className="grid gap-6">
                    {currentSection.subsections.map((subsection, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {subsection.title}
                        </h3>
                        <div className="prose prose-sm max-w-none text-gray-600">
                          {subsection.content.split('\n').map((line, i) => (
                            <p key={i} className="mb-2">{line}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}